import { RunPayrollSchemaContract } from 'schemas/RunPayrollSchema';

export const planTypes = {
  FLAT: 'FLAT',
  DEFAULT: 'DEFAULT',
} as const;

export const renewalPeriods = {
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  HALF_YEARLY: 'HALF-YEARLY',
  ANNUAL: 'ANNUAL',
} as const;

type PricingDetails = NonNullable<
  NonNullable<RunPayrollSchemaContract['requiredAmount']>['opfin-charges']
>['pricingDetails'];

export const getHasOverageFees = (pricingDetails: PricingDetails) => {
  return !!(pricingDetails && pricingDetails.overage_charges && pricingDetails.overage_charges > 0);
};

export const getHasRenewalFees = (pricingDetails: PricingDetails) => {
  return !!(
    pricingDetails &&
    pricingDetails.base_charges &&
    pricingDetails.base_charges > 0 &&
    !pricingDetails.base_fee_already_paid
  );
};

export const getRenewalPeriod = (renewalPeriod?: string | null) => {
  if (!renewalPeriod) {
    return renewalPeriods.MONTHLY;
  }
  return renewalPeriod;
};

export const getRenewalPeriodText = (renewalPeriod?: string | null) => {
  if (renewalPeriod === renewalPeriods.QUARTERLY) {
    return { plan: 'Quarterly', duration: 'quarter' };
  }
  if (renewalPeriod === renewalPeriods.HALF_YEARLY) {
    return { plan: 'Half Yearly', duration: 'six months' };
  }
  if (renewalPeriod === renewalPeriods.ANNUAL) {
    return { plan: 'Yearly', duration: 'year' };
  }
  return { plan: 'Monthly', duration: 'month' };
};

export const getTooltipContent = (pricingDetails: PricingDetails) => {
  const hasOverageFees = getHasOverageFees(pricingDetails);
  const hasRenewalFees = getHasRenewalFees(pricingDetails);
  const renewalPeriod = getRenewalPeriod(pricingDetails?.renewal_period);
  const renewalPeriodTexts = getRenewalPeriodText(pricingDetails?.renewal_period);

  if (!pricingDetails || !pricingDetails.total_charges) {
    return undefined;
  }
  if (renewalPeriod === renewalPeriods.MONTHLY && !hasOverageFees) {
    return 'Your monthly plan charges';
  }
  if (renewalPeriod === renewalPeriods.MONTHLY && hasOverageFees) {
    return 'Your monthly plan fee and charges for employees beyond the plan limit';
  }
  if (hasOverageFees && hasRenewalFees) {
    return `Your plan has ended. Renewal fees apply for the next ${renewalPeriodTexts.duration}. Overage charges are for usage beyond your plan limits`;
  }
  if (hasOverageFees) {
    return 'These are overage charges from the usage beyond plan limits';
  }
  if (hasRenewalFees) {
    return `You're all set for another ${renewalPeriodTexts.duration}! Here are your renewal charges.`;
  }
};

export const getUpgradePricingPlanEmailTemplate = ({
  orgName,
  userName,
}: {
  orgName: string;
  userName: string;
}) =>
  `mailto:xpayroll@razorpay.com?subject=Request to Upgrade Pricing Plan - [${encodeURIComponent(
    orgName || 'Organization Name',
  )}]&body=${encodeURIComponent(
    `Dear XPayroll team,

I hope this email finds you well.

We are writing to request information about upgrading our current pricing plan. Our organization has either reached or is approaching the maximum employee limit on our current tier, and we need to transition to the next pricing tier to accommodate our growing team.

Could you please provide details of the next tier pricing plan, including employee capacity limits?

We would appreciate receiving this information at your earliest convenience, as we need to ensure uninterrupted service for our team. If you need any additional information from our end, please let me know.

Thank you for your assistance.

Best regards,
${userName || '[Your Name]'}`,
  )}`;
